import { useCallback } from 'react';
import { Edge, Node, Position, useReactFlow } from 'reactflow';

// @ts-ignore flextree isn't typed
import { flextree } from 'd3-flextree';
import { HierarchyNode, stratify } from 'd3-hierarchy';
import { merge } from 'lodash-es';

import { useSidebarState } from '@components/sidebar';
import { TAG_HEIGHT_LARGE } from '@components/tag/tag';
import { remToPx } from '@utils/converters';

import { JourneyActionValuesTypes } from 'api/types';
import { conditionGapSize } from 'ui/actions/condition-v2/helpers';
import { getNodeHeight } from 'ui/journey-builder/hooks/use-nodes';

import { TravellerJourneyNode } from '../node-types/types';

export { useNodes as useFlowNodes } from 'reactflow';

type LoopNode = {
  child: string;
  parent: string;
  nodeDescription: string;
  description?: string;
};

const layoutWidth = 352;

const layout = flextree<Node>().nodeSize((node: Node) => {
  if (node.data.type === 'journey_branch') {
    return [layoutWidth, 140];
  } else if (node.data.type === 'journey_merge') {
    return [layoutWidth, 90];
  } else if (node.data.type === 'journey_loop') {
    return [layoutWidth, 140];
  } else if (node.data.type === 'branch_base') {
    return [layoutWidth, 72];
  }

  const height = getNodeHeight(node);

  let extraHeight = 24;
  if (node.data.type === 'combo_send_form') {
    extraHeight += 48; // icon height + padding 8px
  }
  if (node.data.type === 'journey_triggers') {
    extraHeight += 72;
  }
  if (
    (node.data.type === 'condition' || node.data.type === 'condition_v2') &&
    node.data.data.isHalted &&
    node.data.data.isCompleted
  ) {
    extraHeight += TAG_HEIGHT_LARGE + remToPx(conditionGapSize);
  }
  if (node.data.type === 'journey_start') {
    extraHeight += -14;
  }
  if (node.data.type === 'break_loop') {
    extraHeight += 24;
  }
  if (node.data.type === 'ghost') {
    return [100, height + 24];
  }
  if (node.data.data.isSkipped) {
    extraHeight += 48;
  }
  return [layoutWidth, height + extraHeight];
});

const mapNodePositions = (nodes: Node[]) => {
  const sortChildren = (a: HierarchyNode<Node>, b: HierarchyNode<Node>) => {
    if (b.data.type === 'ghost') {
      return 1;
    }
    if (a.data.type === 'ghost') {
      return -1;
    }
    const order: string[] = a.parent?.data?.data?.values?.order ?? [];
    return order.indexOf(a.id ?? '') - order.indexOf(b.id ?? '');
  };
  const tree = stratify<Node>()(nodes).sort(sortChildren);

  const root = layout(tree);

  return nodes.map((node) => {
    // find the node in the tree with the same id and get its coordinates
    const { x, y } = root.find((d: Node) => d.id === node.id) || node.position;
    const adjustedX = node.type === 'ghost' ? x + 175 : x;
    const parentNode = root.find((d: Node) => d.id === node.parentId);
    const parentX = parentNode ? parentNode.x : 0;
    const parentY = parentNode ? parentNode.y : 0;

    return {
      ...node,
      sourcePosition: node.sourcePosition,
      targetPosition: node.targetPosition,
      position: { x: adjustedX - parentX, y: y - parentY },
      width: 320,
    };
  });
};

export const useNodes = () => {
  const { getNodes, setNodes: setFlowNodes, setEdges } = useReactFlow();
  const { activeId } = useSidebarState();
  const selectedNode = getNodes().find((node) => node.id === activeId) as TravellerJourneyNode;

  const updateSelectedNode = (changes: Partial<TravellerJourneyNode>) => {
    const updated = merge(selectedNode, changes);
    const nodes = getNodes() as TravellerJourneyNode[];
    const selectedIndex = nodes.findIndex((n) => n.id === updated.id);
    if (selectedIndex > -1) {
      nodes[selectedIndex] = updated;
    }
    setNodes(nodes);
  };

  const updateSelectedNodeData = (changes: Partial<TravellerJourneyNode['data']>) =>
    updateSelectedNode({ data: { ...selectedNode.data, ...changes } });

  const setNodes = useCallback(
    (nodes: TravellerJourneyNode[]) => {
      const newEdges: Edge[] = [];

      const loopGhostNodes: TravellerJourneyNode[] = [];
      const createLoopGhostNodes = (loopActionId: string, breakLoopNode: TravellerJourneyNode) => {
        const loopNode = nodes.find((n) => n.id === loopActionId);
        const loopInProgress =
          breakLoopNode.data.status !== 'completed' && loopNode?.data.status === 'in_progress';
        let currentNode = breakLoopNode;
        const hierarchy: LoopNode[] = [];
        while (currentNode) {
          const ghostId = `${currentNode.id}-ghost`;
          const parentId = `${currentNode.parentId}${
            currentNode.parentId === loopActionId ? '' : '-ghost'
          }`;
          if (currentNode.id === loopActionId) {
            break;
          }
          hierarchy.push({
            child: ghostId,
            parent: parentId,
            nodeDescription: currentNode.data.nodeDescription,
            description: currentNode.data.description,
          });
          const currentParentId = currentNode.parentId;
          currentNode = nodes.find((n) => n.id === currentParentId) as TravellerJourneyNode;
        }
        hierarchy.forEach(({ child, parent, description, nodeDescription }) => {
          loopGhostNodes.push({
            id: child,
            type: 'ghost',
            width: 1,
            height: 1,
            targetPosition: parent === loopActionId ? Position.Top : Position.Bottom,
            sourcePosition: parent === loopActionId ? Position.Top : Position.Bottom,
            parentId: parent,
            data: {
              nodeDescription,
              description,
              isJourneyPaused: false,
              journeyId: '',
              travellerJourneyId: '',
              dependants: [],
              groupActions: [],
            },
          });
          newEdges.push({
            id: `e-${child}`,
            source: parent,
            target: child,
            type: loopInProgress ? 'loopEdgeInProgress' : 'loopEdge',
          });
        });
        const lastNodes = hierarchy
          .filter((node) => !hierarchy.some((item) => item.parent === node.child))
          .map((node) => node.child);
        lastNodes.forEach((lastNode) => {
          newEdges.push({
            id: `e-${lastNode}-break-loop`,
            source: breakLoopNode.id,
            target: lastNode,
            type: loopInProgress ? 'loopEdgeInProgress' : 'loopEdge',
            zIndex: -1,
          });
        });
      };

      nodes.forEach((node) => {
        if (node.type === 'journey_start') {
          return;
        }
        const source = node.parentId ?? 'journey_start';
        const target = node.id;
        const isSucceedEdge =
          node.data.parentCompleted || (node.data.hasFlowReached && node.data.parentSkipped);
        const edge = {
          id: `e-${source}-${target}`,
          source,
          target,
          ...(isSucceedEdge && {
            type: 'travellerSucceedEdge',
          }),
        };
        newEdges.push(edge);
        if (node.type === 'break_loop') {
          const data = node.data.values as JourneyActionValuesTypes['break_loop'];
          if (data?.loopAction) {
            createLoopGhostNodes(data.loopAction, node);
          }
        }
        if (node.data.dependants) {
          node.data.dependants.forEach((dependant) => {
            const additionalEdge = {
              id: `e-${dependant}-${target}`,
              source: dependant,
              target,
              animated: true,
              type: 'travellerExtraEdge',
            };
            newEdges.push(additionalEdge);
          });
        }
      });
      setEdges(newEdges);
      const newNodes = mapNodePositions([...nodes, ...loopGhostNodes] as Node[]);
      setFlowNodes(newNodes);
    },
    [setEdges, setFlowNodes]
  );

  return {
    setNodes,
    selectedNode,
    updateSelectedNodeData,
    getNodes,
  };
};
